import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../supabase';
import {
  applySessionContext,
  clearSessionContext,
  fetchUserAccount,
  getDashboardPath,
  registerGymAdmin,
  signIn,
  signOut,
  validateAdminSubscription,
  markPasswordRecovery,
  SUBSCRIPTION_EXPIRED_MESSAGE,
} from '../services/auth/authApi';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authNotice, setAuthNotice] = useState('');
  const authEpochRef = useRef(0);

  const loadAccount = useCallback(async (sessionUser) => {
    const epoch = authEpochRef.current;

    if (!sessionUser) {
      if (epoch !== authEpochRef.current) return;
      setUser(null);
      setAccount(null);
      clearSessionContext();
      return;
    }

    let profile;
    try {
      profile = await fetchUserAccount(sessionUser.id, sessionUser.email);
    } catch (error) {
      if (epoch !== authEpochRef.current) return;
      setUser(null);
      setAccount(null);
      setAuthNotice(error.message || 'Could not load your account.');
      return;
    }
    if (epoch !== authEpochRef.current) return;

    if (!profile) {
      setUser(null);
      setAccount(null);
      clearSessionContext();
      return;
    }

    if (profile.role === 'admin' || profile.role === 'receptionist') {
      if (!profile.gym_id) {
        await supabase.auth.signOut();
        if (epoch !== authEpochRef.current) return;
        clearSessionContext();
        setUser(null);
        setAccount(null);
        setAuthNotice('This account is not linked to a gym. Contact support to fix user_accounts.gym_id.');
        return;
      }
    }

    if (profile.role === 'admin') {
      const check = await validateAdminSubscription(profile.gym_id);
      if (epoch !== authEpochRef.current) return;
      if (!check.valid) {
        await supabase.auth.signOut();
        if (epoch !== authEpochRef.current) return;
        clearSessionContext();
        setUser(null);
        setAccount(null);
        setAuthNotice(check.reason || SUBSCRIPTION_EXPIRED_MESSAGE);
        return;
      }
    }

    if (epoch !== authEpochRef.current) return;
    setUser(sessionUser);
    setAccount(profile);
    applySessionContext(profile);
  }, []);

  useEffect(() => {
    let mounted = true;

    async function init() {
      try {
        const { data } = await supabase.auth.getSession();
        if (mounted) {
          await loadAccount(data.session?.user ?? null);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    }

    init();

    const { data: listener } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!mounted) return;
      if (event === 'PASSWORD_RECOVERY') {
        markPasswordRecovery();
      }
      await loadAccount(session?.user ?? null);
    });

    return () => {
      mounted = false;
      listener.subscription.unsubscribe();
    };
  }, [loadAccount]);

  const login = useCallback(async (email, password, options = {}) => {
    const epoch = ++authEpochRef.current;
    setAuthNotice('');

    try {
      const result = await signIn(email, password, options);
      if (epoch !== authEpochRef.current) return result;
      setUser(result.user);
      setAccount(result.account);
      return result;
    } catch (err) {
      authEpochRef.current += 1;
      setUser(null);
      setAccount(null);
      clearSessionContext();
      throw err;
    }
  }, []);

  const logout = useCallback(async () => {
    authEpochRef.current += 1;
    await signOut();
    setUser(null);
    setAccount(null);
    setAuthNotice('');
  }, []);

  const clearAuthNotice = useCallback(() => setAuthNotice(''), []);

  const updateAccount = useCallback((patch) => {
    setAccount((prev) => {
      if (!prev) return prev;
      const next = { ...prev, ...patch };
      import('../services/offline/accountCache')
        .then(({ saveCachedAccount }) => saveCachedAccount(next, user))
        .catch(() => {});
      return next;
    });
    if (patch?.email) {
      setUser((prev) => (prev ? { ...prev, email: patch.email } : prev));
    }
  }, [user]);

  const register = useCallback(async (payload) => {
    const result = await registerGymAdmin(payload);
    if (result.session) {
      setUser(result.user);
      setAccount(result.account);
    }
    return result;
  }, []);

  const dashboardPath = useMemo(
    () => (account?.role ? getDashboardPath(account.role) : '/'),
    [account?.role],
  );

  const value = useMemo(
    () => ({
      user,
      account,
      loading,
      isAuthenticated: Boolean(user && account),
      role: account?.role || null,
      dashboardPath,
      authNotice,
      clearAuthNotice,
      login,
      logout,
      register,
      updateAccount,
    }),
    [user, account, loading, dashboardPath, authNotice, clearAuthNotice, login, logout, register, updateAccount],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
